import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  CalendarDays,
  ChevronRight,
  Clock3,
  Inbox,
  LoaderCircle,
  MessageSquareWarning,
  RadioTower,
  Send,
  X,
} from "lucide-react";
import api from "../api/api";
import "../css/SupportHistoryDrawer.css";

const SupportHistoryDrawer = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ installations: [], reclamations: [] });
  const [activeType, setActiveType] = useState("installation");
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = "hidden";
    setLoading(true);
    setError("");
    api
      .get("/support/my-tickets")
      .then((response) =>
        setData({
          installations: response.data?.installations || [],
          reclamations: response.data?.reclamations || [],
        }),
      )
      .catch((requestError) => {
        console.error("Support history loading failed:", requestError);
        setError(
          t("support_load_error", "Impossible de charger vos demandes."),
        );
      })
      .finally(() => setLoading(false));
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, t]);

  useEffect(() => {
    if (!isOpen) {
      setSelected(null);
      setMessages([]);
      setReply("");
    }
  }, [isOpen]);

  const tickets = useMemo(
    () =>
      activeType === "installation" ? data.installations : data.reclamations,
    [activeType, data],
  );

  const formatDate = (value, withTime = false) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat(i18n.language || "fr", {
      dateStyle: "medium",
      ...(withTime ? { timeStyle: "short" } : {}),
    }).format(date);
  };

  const getStatus = (ticket, type = activeType) =>
    type === "installation"
      ? ticket.workflow_status || "new"
      : ticket.support_status || "new";

  const openTicket = async (ticket) => {
    setSelected({ ...ticket, type: activeType });
    setMessagesLoading(true);
    setError("");
    try {
      const response = await api.get(
        `/support/${activeType}/${ticket.id}/messages`,
      );
      setMessages(response.data || []);
    } catch (requestError) {
      console.error("Support messages loading failed:", requestError);
      setMessages([]);
      setError(
        t("support_messages_error", "Impossible de charger la conversation."),
      );
    } finally {
      setMessagesLoading(false);
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim() || sending) return;
    setSending(true);
    setError("");
    try {
      const response = await api.post(
        `/support/${selected.type}/${selected.id}/messages`,
        { message: reply.trim() },
      );
      setMessages((previous) => [...previous, response.data]);
      setReply("");
    } catch (requestError) {
      console.error("Support reply failed:", requestError);
      setError(t("support_reply_error", "Impossible d’envoyer votre message."));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sf-client-support-layer" dir={i18n.dir()}>
      <button
        className="sf-client-support-overlay"
        onClick={onClose}
        aria-label={t("close", "Fermer")}
      />
      <aside
        className="sf-client-support-drawer"
        role="dialog"
        aria-modal="true"
      >
        <header>
          {selected ? (
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setMessages([]);
              }}
              aria-label={t("back", "Retour")}
            >
              <ArrowLeft size={19} />
            </button>
          ) : (
            <span className="sf-client-support-mark">
              <Inbox size={19} />
            </span>
          )}
          <div>
            <span>{t("support_customer_space", "ESPACE CLIENT")}</span>
            <h2>
              {selected
                ? `#${selected.id}`
                : t("my_support_requests", "Mes demandes")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("close", "Fermer")}
          >
            <X size={19} />
          </button>
        </header>

        {!selected ? (
          <>
            <nav className="sf-client-support-tabs">
              <button
                className={activeType === "installation" ? "is-active" : ""}
                onClick={() => setActiveType("installation")}
              >
                <RadioTower size={17} />
                {t("support_installations", "Installations")}
                <b>{data.installations.length}</b>
              </button>
              <button
                className={activeType === "reclamation" ? "is-active" : ""}
                onClick={() => setActiveType("reclamation")}
              >
                <MessageSquareWarning size={17} />
                {t("support_claims", "Réclamations")}
                <b>{data.reclamations.length}</b>
              </button>
            </nav>
            <div className="sf-client-support-list">
              {loading ? (
                <div className="sf-client-support-empty">
                  <LoaderCircle className="is-spinning" />
                  <p>{t("loading", "Chargement...")}</p>
                </div>
              ) : error ? (
                <div className="sf-client-support-error">{error}</div>
              ) : tickets.length === 0 ? (
                <div className="sf-client-support-empty">
                  <Inbox size={36} />
                  <h3>{t("support_no_request", "Aucune demande")}</h3>
                  <p>
                    {t(
                      "support_no_request_client_hint",
                      "Vos prochaines demandes apparaîtront ici.",
                    )}
                  </p>
                </div>
              ) : (
                tickets.map((ticket) => {
                  const status = getStatus(ticket);
                  return (
                    <button
                      className="sf-client-ticket"
                      key={ticket.id}
                      onClick={() => openTicket(ticket)}
                    >
                      <span>
                        {activeType === "installation" ? (
                          <RadioTower size={19} />
                        ) : (
                          <MessageSquareWarning size={19} />
                        )}
                      </span>
                      <div>
                        <strong>
                          {activeType === "installation"
                            ? ticket.field_name ||
                              t(
                                "sensor_installation",
                                "Installation du capteur",
                              )
                            : ticket.contenu}
                        </strong>
                        <small>
                          <Clock3 size={12} />
                          {formatDate(ticket.created_at || ticket.date)}
                        </small>
                      </div>
                      <em className={`is-${status}`}>
                        {t(`support_status_${status}`, status)}
                      </em>
                      <ChevronRight size={18} />
                    </button>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <div className="sf-client-support-detail">
            <section className="sf-client-status-card">
              <div>
                <span>{t("status", "Statut")}</span>
                <strong className={`is-${getStatus(selected, selected.type)}`}>
                  {t(
                    `support_status_${getStatus(selected, selected.type)}`,
                    getStatus(selected, selected.type),
                  )}
                </strong>
              </div>
              {selected.type === "installation" && selected.field_name && (
                <div>
                  <span>{t("field", "Champ")}</span>
                  <strong>{selected.field_name}</strong>
                </div>
              )}
              {selected.type === "installation" && selected.scheduled_at && (
                <div>
                  <span>{t("support_appointment", "Rendez-vous")}</span>
                  <strong>
                    <CalendarDays size={14} />
                    {formatDate(selected.scheduled_at, true)}
                  </strong>
                </div>
              )}
            </section>

            {selected.type === "reclamation" && (
              <section className="sf-client-original">
                <span>{t("your_message", "Votre message")}</span>
                <p>{selected.contenu}</p>
              </section>
            )}

            <section className="sf-client-conversation">
              <div className="sf-client-conversation-title">
                <Send size={17} />
                <div>
                  <h3>{t("support_conversation", "Conversation")}</h3>
                  <p>
                    {t(
                      "support_team_reply_hint",
                      "Suivez ici les réponses de l’équipe SmartFarm.",
                    )}
                  </p>
                </div>
              </div>
              <div className="sf-client-messages">
                {messagesLoading ? (
                  <LoaderCircle className="is-spinning" />
                ) : messages.length === 0 ? (
                  <p className="sf-client-no-message">
                    {t(
                      "support_waiting_reply",
                      "Votre demande est enregistrée. L’équipe vous répondra ici.",
                    )}
                  </p>
                ) : (
                  messages.map((message) => (
                    <article
                      key={message.id}
                      className={`is-${message.sender_role}`}
                    >
                      <div>
                        <strong>
                          {message.sender_role === "admin"
                            ? t("support_team", "Équipe SmartFarm")
                            : t("you", "Vous")}
                        </strong>
                        <time>{formatDate(message.created_at, true)}</time>
                      </div>
                      <p>{message.message}</p>
                    </article>
                  ))
                )}
              </div>
              {error && <div className="sf-client-support-error">{error}</div>}
              <div className="sf-client-compose">
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  placeholder={t(
                    "support_client_reply_placeholder",
                    "Ajouter un message...",
                  )}
                />
                <button onClick={sendReply} disabled={!reply.trim() || sending}>
                  {sending ? (
                    <LoaderCircle className="is-spinning" size={16} />
                  ) : (
                    <Send size={16} />
                  )}
                  {t("send", "Envoyer")}
                </button>
              </div>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
};

export default SupportHistoryDrawer;
